import { ref, computed, watch, Ref, ComputedRef } from 'vue';
import { KitchenTicket, KitchenTicketItem, OrderTicket } from 'app-core';
import { useAppStore } from '@/stores/useAppStore';
import { useRoute } from 'vue-router';
import { emitter } from '@/utilities/emitter';

import moment from 'moment-timezone'

import { Item } from './useKitchenTicketItems';
export interface ProcessedTicket  {
  id: string;
  ticket: KitchenTicket;
  warningColor: string;
  page: number;
}

type ProcessedTickets = (ProcessedTicket | undefined)[][];

const audio = new Audio('/audio/alert2.mp3');
const firstLoad = ref(false);
const openPage: Ref<number> = ref(1);
const donePage: Ref<number> = ref(1);
const selectedItems: Ref<Item[]> = ref([]);
let fetchLatestInterval: ReturnType<typeof setInterval> | null = null;

const useKitchenTickets = () => {
  const store = useAppStore();
  const route = useRoute();

  const yellowTimer = computed(() => store.kdsSettings.yellowTimer);
  const redTimer = computed(() => store.kdsSettings.redTimer);
  const ticketsPerPage = computed(() => store.kdsSettings.itemsPerPage[0] * store.kdsSettings.itemsPerPage[1]);
  const gridRows = computed(() => store.kdsSettings.itemsPerPage[0]);
  const gridCols = computed(() => store.kdsSettings.itemsPerPage[1])
  const isDonePage = computed(() => route.name === 'kitchenDoneTickets');
  const page = computed(() => (route.name === 'kitchenDoneTickets' ? donePage.value : openPage.value));
  const openTicketsCount = computed(() => openTickets.value.length);
  const doneTicketsCount = computed(() => doneTickets.value.length);
  const totalOpenPages = computed(() => Math.ceil(openTicketsCount.value / ticketsPerPage.value));
  const totalDonePages = computed(() => Math.ceil(doneTicketsCount.value / ticketsPerPage.value));

  const kitchenTickets = computed(() => {
    const tickets = [...KitchenTicket.all()].filter(kt => {
      if (store.user && store.kdsSettings.filterForUser) {
        return kt.user_id == store.user.id
      }

      return true
    })

    const sortedTickets = tickets.sort((a, b) => {
      return moment(b.issued_at).diff(moment(a.issued_at));
    });

    return sortedTickets.map((ticket) => {
      let warningColor = 'ongoing';
      const duration = moment.duration(moment().diff(moment(ticket.issued_at)));
      const hours = Math.floor(duration.asHours());
      const minutes = Math.floor(duration.asMinutes()) - hours * 60;
      const time = hours * 60 + minutes;

      if (time >= yellowTimer.value && time < redTimer.value) {
        warningColor = 'yellow';
      }

      if (time >= redTimer.value) {
        warningColor = 'red';
      }

      return {
        id: ticket.hash,
        ticket: ticket,
        warningColor: warningColor,
        page: 1,
      };
    });
  });

  const openTickets = computed(() => {
    const filtered = kitchenTickets.value.filter((ticket) => ticket.ticket.isPending());
    return filtered.map((ticket, index) => {
      ticket.page = Math.floor(index / ticketsPerPage.value) + 1;
      return ticket;
    });
  });

  const doneTickets = computed(() => {
    const filtered = kitchenTickets.value.filter((ticket) => ticket.ticket.isDone());
    return filtered.map((ticket, index) => {
      ticket.page = Math.floor(index / ticketsPerPage.value) + 1;
      return ticket;
    });
  });

  const filteredTickets = computed(() => {
    let paginatedTickets = openTickets.value.filter((ticket) => ticket.page === page.value);

    if (isDonePage.value) {
      paginatedTickets = doneTickets.value.filter((ticket) => ticket.page === page.value);
    }

    return paginatedTickets;
  });

  const processedTickets: ComputedRef<ProcessedTickets> = computed(() => {
    const tickets = [];

    for (let i = 0; i < gridRows.value; i++) {
      const row = [];

      for (let j = 0; j < gridCols.value; j++) {
        row.push(filteredTickets.value[i * gridCols.value + j]);
      }

      tickets.push(row);
    }

    return tickets;
  });

  watch(
    () => kitchenTickets.value,
    (newValue, oldValue) => {
      if (newValue.length > oldValue.length) {
        audio.play().catch(() => {});
      }
    }
  );

  watch(
    () => ticketsPerPage.value,
    () => {
      openPage.value = 1;
      donePage.value = 1;
    }
  );

  const fetchLatest = async () => {
    if (firstLoad.value) {
      return;
    } else {
      firstLoad.value = true;
    }

    fetchLatestInterval = setInterval(() => {
      KitchenTicket.fetchLatest();
    }, 60 * 1000);
  };

  const clearFetchLatestInterval = () => {
    firstLoad.value = false;

    if (fetchLatestInterval !== null) {
      clearInterval(fetchLatestInterval);
      fetchLatestInterval = null;
    }
  };

  const completeTicket = async (ticket: KitchenTicket) => {
    const kitchenTicket = KitchenTicket.find(ticket.hash);
    kitchenTicket?.complete();

    if (kitchenTicket?.order_ticket_hash) {
      const result = await OrderTicket.onKitchenTicketDone(kitchenTicket.order_ticket_hash)
      if (result?.failed) {
        emitter.emit('orderTicketError', {
          externalCode: result.externalCode,
          error: 'mark_ready_failed',
        })
      }
    }
  };

  const recallTicket = (ticket: KitchenTicket) => {
    const kitchenTicket = KitchenTicket.find(ticket.hash);
    kitchenTicket?.recall();
  };

  const completeItem = (ticket: KitchenTicket, item: KitchenTicketItem, complete: boolean, sync: boolean = true) => {
    const kitchenTicket = KitchenTicket.find(ticket.hash);
    kitchenTicket?.completeItem(item, complete, sync);
  };

  const refreshTickets = () => {
    KitchenTicket.fetchLatest(true);
  };

  return {
    kitchenTickets,
    openTickets,
    doneTickets,
    openTicketsCount,
    doneTicketsCount,
    selectedItems,
    gridRows,
    gridCols,
    openPage,
    donePage,
    totalOpenPages,
    totalDonePages,
    processedTickets,
    fetchLatest,
    clearFetchLatestInterval,
    refreshTickets,
    completeTicket,
    recallTicket,
    completeItem,
  };
};

export default useKitchenTickets;
